import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { ChartCard } from './ChartCard'
import { formatBytes } from './format'
import type { MemoryLatest, SeriesPoint } from '../../types/monitoring'

export function MemoryPanel({ latest, series, loading, error }: {
  latest: MemoryLatest | undefined; series: SeriesPoint[]; loading: boolean; error?: string
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <ChartCard title="Memory Usage" subtitle="RAM + swap utilization over time" loading={loading} error={error} isEmpty={!series.length}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={series} margin={{ left: -20, right: 8 }}>
              <defs>
                <linearGradient id="memGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="swapGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f97316" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false}
                tickFormatter={(t: string) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v: number, name: string) => [`${v.toFixed(1)}%`, name === 'percent' ? 'Memory' : 'Swap']}
                labelFormatter={(l) => new Date(l).toLocaleString()} />
              <Area type="monotone" dataKey="percent" stroke="#4f46e5" strokeWidth={2} fill="url(#memGradient)" isAnimationActive={false} />
              <Area type="monotone" dataKey="swap_percent" stroke="#f97316" strokeWidth={2} fill="url(#swapGradient)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
        <h2 className="font-bold text-secondary mb-4">Memory Breakdown</h2>
        {loading ? (
          <div className="h-56 bg-neutral-100 rounded-lg animate-pulse" />
        ) : (
          <div className="space-y-3 text-sm">
            <Row label="Total" value={formatBytes(latest?.total)} />
            <Row label="Used" value={formatBytes(latest?.used)} />
            <Row label="Available" value={formatBytes(latest?.available)} />
            <Row label="Free" value={formatBytes(latest?.free)} />
            <div className="h-px bg-neutral-100 my-2" />
            <Row label="Swap total" value={formatBytes(latest?.swap_total)} />
            <Row label="Swap used" value={formatBytes(latest?.swap_used)} />
          </div>
        )}
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-neutral-50 last:border-0">
      <span className="text-neutral-500">{label}</span>
      <span className="font-semibold text-neutral-800 tabular-nums">{value}</span>
    </div>
  )
}
